import React, {useMemo} from 'react';
import type {ManagerArtist} from '../../types';
import type {BoxProps} from '@tempo/core';
import {Tr} from '@tempo/core';
import {useTranslation} from 'next-i18next';
import type {Column} from '@components/Table/types';
import type {TableContextType} from '@components/Table';
import Table, {TableContext} from '@components/Table';
import TableHeader from '@components/Table/core/TableHeader';
import HeaderCell from '@components/Table/cells/HeaderCell';
import {ArtistTableColumn} from './types';
import ArtistTableBody from './ArtistTableBody';

type ArtistTableProps = {
	artists: ManagerArtist[];
	onArtistEdit: (artist: ManagerArtist) => void;
	onArtistRemove: (artist: ManagerArtist) => void;
} & BoxProps;

const ArtistTableContext = TableContext as TableContextType<ArtistTableColumn>;

const ArtistTable = ({
	artists,
	onArtistEdit,
	onArtistRemove,
	...rest
}: ArtistTableProps) => {
	const {t} = useTranslation('d4c');
	const columns: Column<ArtistTableColumn>[] = useMemo(() => {
		return [
			{
				id: ArtistTableColumn.NAME,
				label: t('analyticsartist_text_name_d4c-web'),
				width: '160px',
			},
			{
				id: ArtistTableColumn.UPC,
				label: t('deliverymonitoring_text_upc_d4c-web'),
				display: {base: false, sm: true},
				width: '160px',
			},
			{
				id: ArtistTableColumn.EDIT,
				width: '48px',
			},
			{
				id: ArtistTableColumn.DELETE,
				width: '48px',
			},
		];
	}, [t]);

	return (
		<Table
			columns={columns}
			flex={1}
			layout="fixed"
			variant="contained"
			{...rest}
		>
			<ArtistTableContext.Consumer>
				{({columns}) => (
					<>
						<TableHeader>
							<Tr>
								{columns.map(column => {
									return (
										<HeaderCell
											key={column.id}
											label={column.label || ''}
											width={column.width}
											display={column.display}
										/>
									);
								})}
							</Tr>
						</TableHeader>
						<ArtistTableBody
							data={artists}
							onArtistEdit={onArtistEdit}
							onArtistRemove={onArtistRemove}
						/>
					</>
				)}
			</ArtistTableContext.Consumer>
		</Table>
	);
};

export default ArtistTable;
